import { ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';

import { classNames } from 'support/helpers/generic/generic';

type PopupWidth = 'fit-content' | 'sm' | 'md' | 'lg' | 'xl';

const sizeClasses = {
  'fit-content': undefined,
  sm: 'sm:w-full sm:max-w-md',
  md: 'sm:w-full sm:max-w-lg',
  lg: 'sm:w-full sm:max-w-2xl',
  xl: 'sm:w-full sm:max-w-5xl',
};

const Wrapper = ({
  children,
  open,
  setOpen,
  initialFocus,
  testId,
  width = 'md',
  className,
  shouldGrowIndefinitely,
}: {
  testId?: string;
  initialFocus?: React.RefObject<HTMLElement>;
  children: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  width?: PopupWidth;
  className?: string;
  shouldGrowIndefinitely?: boolean;
}) => {
  return (
    <Dialog as="div" transition className="relative z-50" open={open} onClose={setOpen} initialFocus={initialFocus}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 duration-300 ease-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex h-full max-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            data-testid={testId}
            transition
            className={classNames(
              'relative overflow-y-visible bg-white text-left shadow-xl sm:my-8 rounded-lg duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
              sizeClasses[width],
              shouldGrowIndefinitely ? '!max-w-[75%]' : undefined,
              className,
            )}
          >
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export const Footer = ({ children, title, action }: { children: ReactNode; title: string; action?: ReactNode }) => {
  return (
    <div className="flex items-center gap-4 rounded-b-lg bg-gray-50 px-6 py-4">
      <div>
        {title && <div className="text-sm font-medium text-black">{title}</div>}
        <div className="text-sm text-gray-900">{children}</div>
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
};

export const Header = ({ children, title }: { children?: ReactNode; title: string }) => {
  return (
    <div className="rounded-t-lg px-6 pt-6">
      <DialogTitle as="h3" className="text-lg font-medium text-gray-900">
        {title}
      </DialogTitle>
      <p className="text-sm text-gray-500">{children}</p>
    </div>
  );
};

export const Body = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={classNames('p-6', className)}>{children}</div>;
};
export const BasePopup = Object.assign(Wrapper, { Footer, Header, Body });
